import { FormEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loginWithEmail, registerWithEmail, requestPasswordReset } from '../api/authClient';
import {
  useAuthStore,
  selectAuthStatus,
  selectAuthError,
  selectAuthDraft,
  AuthMode
} from '../state/authStore';
import { policyMetadata, buildPolicyAcceptancePayload } from '../../../shared/config/policyMetadata';
import {
  needsPolicyReacceptance,
  recordPolicyAcceptance,
  resolvePolicyLocale
} from '../../../shared/config/policyAcceptanceStorage';
import { GoogleOAuthButton } from './GoogleOAuthButton';
import { PURCHASE_VOUCHER_URL } from '../../../shared/config/externalLinks';

interface EmailAuthFormProps {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
}

export function EmailAuthForm({ mode, onModeChange }: EmailAuthFormProps) {
  const { t } = useTranslation();
  const { setAuthenticating, setAuthenticated, setError } = useAuthStore();
  const status = useAuthStore(selectAuthStatus);
  const error = useAuthStore(selectAuthError);
  const draft = useAuthStore(selectAuthDraft);
  const updateDraft = useAuthStore((state) => state.updateDraft);

  const [resetSuccess, setResetSuccess] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);
  const [showPolicyReminder, setShowPolicyReminder] = useState(false);

  const emailId = useId();
  const passwordId = useId();
  const confirmPasswordId = useId();
  const usernameId = useId();

  const { email, password, confirmPassword, username, hasAgreed, referralCode } = draft;

  useEffect(() => {
    setError(null);
    setLocalError(null);
    if (mode !== 'forgotPassword') {
      setResetSuccess(false);
    }
  }, [mode, setError]);

  useEffect(() => {
    if (mode === 'forgotPassword') {
      setResetSuccess(false);
      setLocalError(null);
    }
  }, [email, mode]);

  useEffect(() => {
    setShowPolicyReminder(needsPolicyReacceptance());
  }, []);

  const isSubmitting = status === 'authenticating' || isResetSubmitting;
  const feedbackError = mode === 'forgotPassword' ? localError : error;

  const acknowledgePolicyReminder = () => {
    const payload = buildPolicyAcceptancePayload(resolvePolicyLocale());
    recordPolicyAcceptance(payload);
    setShowPolicyReminder(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      const message = t('account.errors.missingEmail');
      if (mode === 'forgotPassword') {
        setLocalError(message);
      } else {
        setError(message);
      }
      return;
    }

    if (mode === 'forgotPassword') {
      setIsResetSubmitting(true);
      setLocalError(null);
      try {
        await requestPasswordReset({ email });
        setResetSuccess(true);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('account.errors.generic');
        setLocalError(message);
      } finally {
        setIsResetSubmitting(false);
      }
      return;
    }

    if (!password) {
      setError(t('account.errors.missingPassword'));
      return;
    }

    if (mode === 'register') {
      if (password !== confirmPassword) {
        setError(t('account.errors.passwordMismatch'));
        return;
      }
      if (!hasAgreed) {
        setError(t('account.errors.acceptPolicies'));
        return;
      }
    }

    setAuthenticating();
    try {
      if (mode === 'register') {
        const trimmedUsername = username?.trim();
        const derivedUsername = trimmedUsername && trimmedUsername.length > 0
          ? trimmedUsername
          : email.split('@')[0];
        const token = await registerWithEmail({
          email,
          password,
          username: derivedUsername,
          referralCode
        });
        setAuthenticated(email, token, derivedUsername);
      } else {
        const token = await loginWithEmail({ email, password });
        const derivedUsername = email.split('@')[0];
        setAuthenticated(email, token, derivedUsername);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('account.errors.generic');
      setError(message);
    }
  };

  const renderPasswordField = mode !== 'forgotPassword';
  const renderConfirmField = mode === 'register';

  const passwordField = renderPasswordField ? (
    <div className="space-y-1.5">
      <label htmlFor={passwordId} className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs">
        {t('account.passwordLabel')}
      </label>
      <input
        id={passwordId}
        type="password"
        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        value={password}
        onChange={(event) => updateDraft({ password: event.target.value })}
        className="w-full rounded-xl border border-ai-surface-glow/40 bg-ai-surface-elevated/70 px-4 py-2.5 text-sm text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:py-3"
        placeholder={t('account.passwordPlaceholder')}
        disabled={isSubmitting}
        required
        minLength={8}
      />
    </div>
  ) : null;

  const confirmPasswordField = renderConfirmField ? (
    <div className="space-y-1.5">
      <label
        htmlFor={confirmPasswordId}
        className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs"
      >
        {t('account.confirmPasswordLabel')}
      </label>
      <input
        id={confirmPasswordId}
        type="password"
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(event) => updateDraft({ confirmPassword: event.target.value })}
        className="w-full rounded-xl border border-ai-surface-glow/40 bg-ai-surface-elevated/70 px-4 py-2.5 text-sm text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:py-3"
        placeholder={t('account.confirmPasswordPlaceholder')}
        disabled={isSubmitting}
        required
        minLength={8}
      />
    </div>
  ) : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" aria-label={t('account.form.title')}>
      {mode === 'register' && referralCode ? (
        <div className="rounded-2xl border border-ai-primary/30 bg-ai-primary/5 p-4 text-xs text-ai-text-secondary shadow-ai-card sm:text-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-semibold text-ai-text-primary">
              {t('account.referral.notice', { code: referralCode })}
            </p>
            <button
              type="button"
              className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary transition-colors hover:text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:text-xs"
              onClick={handleClearReferral}
            >
              {t('account.referral.clear')}
            </button>
          </div>
        </div>
      ) : null}
      {showPolicyReminder ? (
        <div
          role="status"
          className="rounded-2xl border border-ai-warning/40 bg-ai-warning/10 p-4 text-xs text-ai-text-primary shadow-ai-card sm:text-sm"
        >
          <p className="font-semibold text-ai-warning">
            {t('account.policyReminder.title', {
              version: policyMetadata.version
            })}
          </p>
          <p className="mt-2 text-ai-text-secondary">
            {t('account.policyReminder.body', {
              version: policyMetadata.version,
              effectiveDate: policyMetadata.effectiveDate
            })}
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <a
              href="/legal/terms"
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-ai-warning/40 px-4 py-2 font-semibold text-ai-warning underline-offset-4 hover:bg-ai-warning/20 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            >
              {t('account.termsLink')}
            </a>
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-ai-warning/40 px-4 py-2 font-semibold text-ai-warning underline-offset-4 hover:bg-ai-warning/20 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            >
              {t('account.privacyLink')}
            </a>
            <button
              type="button"
              className="rounded-full border border-ai-warning/60 bg-ai-warning px-4 py-2 font-semibold text-black shadow-ai-card transition-colors hover:bg-ai-warning/90 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              onClick={acknowledgePolicyReminder}
            >
              {t('account.policyReminder.cta')}
            </button>
          </div>
        </div>
      ) : null}

      {/* Google OAuth 登录按钮 - 仅在登录和注册模式显示 */}
      {(mode === 'login' || mode === 'register') && (
        <>
          <GoogleOAuthButton
            disabled={isSubmitting}
            onError={(error) => setError(error.message)}
          />

          {/* 分隔线 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-ai-surface-glow/30" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-ai-surface-elevated px-2 text-ai-text-secondary">
                {t('account.orDivider')}
              </span>
            </div>
          </div>
        </>
      )}

      <div className="space-y-1.5">
        <label htmlFor={emailId} className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs">
          {t('account.emailLabel')}
        </label>
        <input
          id={emailId}
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => updateDraft({ email: event.target.value })}
          className="w-full rounded-xl border border-ai-surface-glow/40 bg-ai-surface-elevated/70 px-4 py-2.5 text-sm text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:py-3"
          placeholder={t('account.emailPlaceholder')}
          disabled={isSubmitting}
          required
        />
      </div>

      {mode === 'register' ? (
        <div className="space-y-4 sm:space-y-5">
          <div className="space-y-1.5">
            <label htmlFor={usernameId} className="text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-ai-text-secondary sm:text-xs">
              {t('account.usernameLabel')}
            </label>
            <input
              id={usernameId}
              type="text"
              autoComplete="username"
              value={username}
              onChange={(event) => updateDraft({ username: event.target.value })}
              className="w-full rounded-xl border border-ai-surface-glow/40 bg-ai-surface-elevated/70 px-4 py-2.5 text-sm text-ai-text-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:py-3"
              placeholder={t('account.usernamePlaceholder')}
              disabled={isSubmitting}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
            {passwordField}
            {confirmPasswordField}
          </div>
        </div>
      ) : (
        passwordField
      )}

      {mode === 'register' ? (
        <label className="flex items-start gap-3 rounded-xl border border-transparent bg-ai-surface-elevated/50 px-4 py-3 text-[0.7rem] text-ai-text-secondary transition-colors focus-within:border-ai-primary/60 sm:text-xs">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 rounded border-ai-surface-glow/40 bg-ai-surface-elevated/90 text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            checked={hasAgreed}
            onChange={(event) => updateDraft({ hasAgreed: event.target.checked })}
            disabled={isSubmitting}
            required
          />
          <span>
            {t('account.policyPrompt')}{' '}
            <a
              href="/legal/terms"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-ai-primary underline-offset-4 hover:underline"
            >
              {t('account.termsLink')}
            </a>{' '}
            {t('account.policyAnd')}
            <a
              href="/legal/privacy"
              target="_blank"
              rel="noreferrer"
              className="ml-1 font-semibold text-ai-primary underline-offset-4 hover:underline"
            >
              {t('account.privacyLink')}
            </a>
          </span>
        </label>
      ) : null}

      {feedbackError ? (
        <div role="alert" className="rounded-xl border border-ai-danger/40 bg-ai-surface-elevated/70 px-4 py-2.5 text-sm text-ai-danger sm:py-3">
          {feedbackError}
        </div>
      ) : null}

      {resetSuccess ? (
        <div
          role="status"
          className="rounded-xl border border-ai-positive/40 bg-ai-surface-elevated/70 px-4 py-2.5 text-sm text-ai-positive sm:py-3"
        >
          {t('account.reset.success')}
        </div>
      ) : null}

      <button
        type="submit"
        className="w-full rounded-full bg-ai-primary px-5 py-2.5 text-sm font-semibold text-white shadow-ai-glow transition-colors hover:bg-ai-secondary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:opacity-60 sm:px-6 sm:py-3"
        disabled={isSubmitting || (mode === 'register' && !hasAgreed)}
      >
        {isSubmitting
          ? t('account.submitting')
          : t(
            mode === 'login'
              ? 'account.submitLogin'
              : mode === 'register'
                ? 'account.submitRegister'
                : 'account.reset.submit'
          )}
      </button>

      <div className="grid gap-2.5 text-xs text-ai-text-secondary sm:grid-cols-2 sm:gap-3 sm:text-sm">
        <button
          type="button"
          className="rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/60 px-4 py-2 font-medium text-ai-text-primary transition-colors hover:border-ai-primary/60 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:opacity-60 sm:px-6"
          onClick={() => onModeChange(mode === 'forgotPassword' ? 'login' : 'forgotPassword')}
          disabled={isSubmitting}
        >
          {mode === 'forgotPassword' ? t('account.actions.backToLogin') : t('account.actions.forgotPassword')}
        </button>
        <button
          type="button"
          className="rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/60 px-4 py-2 font-medium text-ai-text-primary transition-colors hover:border-ai-primary/60 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:opacity-60 sm:px-6"
          onClick={() => onModeChange(mode === 'register' ? 'login' : 'register')}
          disabled={isSubmitting}
        >
          {mode === 'register' ? t('account.actions.backToLogin') : t('account.actions.goToRegister')}
        </button>
      </div>
      <button
        type="button"
        className="w-full rounded-full bg-gradient-to-r from-[#ff2d55] via-[#ff6f3c] to-[#ffd000] px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.25em] text-black shadow-[0_16px_40px_-12px_rgba(255,45,85,0.75)] transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus disabled:pointer-events-none disabled:opacity-60 sm:px-6 sm:py-3"
        onClick={() => onModeChange('voucher')}
        disabled={isSubmitting}
      >
        {t('account.actions.activateVoucher')}
      </button>
      <a
        href={PURCHASE_VOUCHER_URL}
        target="_blank"
        rel="noreferrer"
        className="inline-flex w-full items-center justify-center rounded-full border border-ai-primary/50 px-5 py-2.5 text-sm font-semibold uppercase tracking-[0.25em] text-ai-primary transition-colors duration-200 hover:border-ai-primary hover:bg-ai-primary/10 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus sm:px-6 sm:py-3"
        aria-label={t('voucherActivation.purchase')}
      >
        {t('voucherActivation.purchase')}
      </a>
    </form>
  );
}
const handleClearReferral = () => {
  updateDraft({ referralCode: '' });
};
